import { _ as _object_spread } from "@swc/helpers/_/_object_spread";
import { _ as _object_spread_props } from "@swc/helpers/_/_object_spread_props";
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useMemo } from 'react';
import { FormProvider } from 'react-hook-form';
import { BiContainer, Box, Button, Column } from 'seagull';
import { Clock, PaperclipAttachment } from 'seagull/icons/essentials';
import { getTestIds } from 'seagull/utils/TestIds';
import { BillMode } from '~/components/BillModal/BillClientModal.types';
import { BillClientModalOld, GetBillClientModalTID } from '~/components/BillModal/BillClientModalOld/BillClientModalOld';
import { getCtaText, normalizeEffectiveDate } from '~/components/BillModal/components/BillingDate/BillingDate.utils';
import { useStoreActions } from '~/contexts';
import { FeatureFlag } from '~/featureFlags';
import { useNonNullableRelationship } from '~/hooks/reactQuery/queries/useRelationship';
import { DEFAULT_MAX_TOTAL_SIZE } from '~/hooks/useAttachFiles';
import { useFeatureFlags } from '~/hooks/useFeatureFlags';
import { useLocaleKeys } from '~/i18n';
import { relationshipModel } from '~/models';
import { BIRelationshipScopeProvider } from '~/providers/BIRelationshipScopeProvider';
import { AddCharge } from '../components/AddCharge/AddCharge';
import { getIssueDateBounds } from '../components/BillingDate/BillingDate.utils';
import { BillItemsTable } from '../components/BillItemsTable/BillItemsTable';
import { BillModalShell } from '../components/BillModalShell/BillModalShell';
import { CreditsWarningCallout, RelationshipEffectiveDateCallout } from '../components/Callouts/Callouts';
import { DateRow } from '../components/DateRow/DateRow';
import { useBillClientForm } from '../hooks/useBillClientForm';
import { BillItemsProvider } from '../hooks/useBillItems';
import { useInvoiceDateContext } from '../hooks/useInvoiceDateContext';
import { isAttachButtonDisabled } from './Attachments/Attachments.utils';
import { AttachmentsList } from './Attachments/AttachmentsList';
import styles from './BillClientModal.module.scss';
import { resolveAttachFilesTooltip } from './BillClientModal.utils';
import { useBillClientBody } from './hooks/useBillClientBody';
import { MapServiceSlot } from './TimeActivities/MapServiceSlot/MapServiceSlot';
import { TimeActivitiesDrawer } from './TimeActivities/TimeActivitiesDrawer/TimeActivitiesDrawer';
export { GetBillClientModalTID };
export const GetBillClientModalBodyTID = (parentTestId)=>getTestIds(parentTestId);
function BillClientModalBody(param) {
    let { props, form, dateContext } = param;
    const { BillClientModalBodyTestIds } = useMemo(()=>GetBillClientModalBodyTID(), []);
    const { t: billTexts } = useLocaleKeys('components.billClientModal');
    const { t: addChargeTexts } = useLocaleKeys('components.billClientModal.addCharge');
    const { t: attachFilesTexts } = useLocaleKeys('invoice.attachFiles');
    const { mode, items, customerName, clientId, agreementOptions, outOfScopeTemplates, formatTemplateCost, invoiceDraftDates, invoiceDateInfo, issueDate, effectiveDate, totalCredits, attachments, timeActivities, onCreateNewCharge, onAddCharge, onSelectItem, onClearItem, onCreateNewItem, onClose, onSubmitClick, showUnselectedItemError } = useBillClientBody({
        props,
        form,
        dateContext
    });
    const issueDateBounds = getIssueDateBounds(dateContext.now, props.earliestBillableDate);
    const attachFilesDisabled = isAttachButtonDisabled({
        attachments: attachments.files,
        isUploading: attachments.isUploading,
        maxTotalSize: DEFAULT_MAX_TOTAL_SIZE
    });
    const attachFilesAction = {
        text: attachFilesTexts.button(),
        iconStart: /*#__PURE__*/ _jsx(PaperclipAttachment, {
            size: 16
        }),
        disabled: attachFilesDisabled,
        tooltipTitle: resolveAttachFilesTooltip({
            isUploading: attachments.isUploading,
            isDisabled: attachFilesDisabled,
            texts: {
                uploading: attachFilesTexts.tooltipUploading(),
                exceededLimit: attachFilesTexts.tooltipExceededLimit({
                    maxSizeMB: DEFAULT_MAX_TOTAL_SIZE / (1024 * 1024)
                })
            }
        }),
        bi: {
            name: 'attach_files'
        },
        testId: BillClientModalBodyTestIds.HeaderAttachFiles(),
        onClick: attachments.openFilePicker
    };
    const showTimeActivitiesAction = {
        text: billTexts.showTimeActivities(),
        iconStart: /*#__PURE__*/ _jsx(Clock, {
            size: 16
        }),
        disabled: !timeActivities.hasSelectableServices,
        tooltipTitle: timeActivities.hasSelectableServices ? undefined : billTexts.noActiveUnitBasedServices(),
        bi: {
            name: 'show_time_activities'
        },
        testId: BillClientModalBodyTestIds.HeaderShowTimeActivities(),
        onClick: timeActivities.openDrawer
    };
    const inlineHeaderActions = [
        attachFilesAction,
        ...timeActivities.allowTimeActivities ? [
            showTimeActivitiesAction
        ] : []
    ];
    const upcomingInvoiceContext = mode === BillMode.Upcoming ? {
        badge: billTexts.upcomingInvoiceBadge(),
        helperText: billTexts.upcomingInvoiceScheduledHelper()
    } : undefined;
    return /*#__PURE__*/ _jsx(_Fragment, {
        children: /*#__PURE__*/ _jsx(BiContainer, {
            name: "bill_client",
            data: {
                relationshipId: props.relationshipId,
                clientId
            },
            children: /*#__PURE__*/ _jsxs(BillModalShell, {
                testId: BillClientModalBodyTestIds.Root(),
                title: billTexts.billClientTitle({
                    customerName
                }),
                onClose: onClose,
                mainAction: /*#__PURE__*/ _jsx(Button, {
                    skin: "primary",
                    bi: {
                        name: 'bill'
                    },
                    testId: BillClientModalBodyTestIds.PrimaryAction(),
                    disabled: items.length === 0,
                    onClick: onSubmitClick,
                    children: mode === BillMode.FutureDate ? billTexts.ctaLabels.scheduleBill() : getCtaText({
                        mode,
                        ctaTexts: billTexts.ctaLabels
                    })
                }),
                additionalActions: {
                    inline: inlineHeaderActions
                },
                children: [
                    timeActivities.hasSelectableServices && /*#__PURE__*/ _jsx(TimeActivitiesDrawer, {
                        open: timeActivities.isDrawerOpen,
                        serviceOptions: timeActivities.serviceOptions,
                        selectedService: timeActivities.selectedService,
                        unusedTimeActivities: timeActivities.unusedTimeActivities,
                        accountingName: timeActivities.accountingName,
                        isSelectedServiceMapped: timeActivities.isSelectedServiceMapped,
                        showAddAll: timeActivities.showAddAll,
                        isRefreshing: timeActivities.isRefreshing,
                        mapServiceSlot: timeActivities.fullSelectedService ? /*#__PURE__*/ _jsx(MapServiceSlot, {
                            service: timeActivities.fullSelectedService,
                            onMapped: timeActivities.onServiceMapped,
                            testId: BillClientModalBodyTestIds.TimeActivities()
                        }) : undefined,
                        onSelectService: timeActivities.onSelectService,
                        onAddSingle: timeActivities.onAddSingle,
                        onAddAll: timeActivities.onAddAll,
                        onRefresh: timeActivities.onRefresh,
                        onClose: timeActivities.closeDrawer,
                        bi: {
                            drawer: {
                                name: 'time_activities_drawer'
                            },
                            serviceOption: {
                                name: 'time_activities_service_option'
                            },
                            actions: {
                                addAll: {
                                    name: 'add_all_time_activities'
                                },
                                refresh: {
                                    name: 'refresh_time_activities'
                                }
                            },
                            card: {
                                card: {
                                    name: 'time_activity_card'
                                },
                                externalLink: {
                                    name: 'open_time_activity_external_link'
                                },
                                add: {
                                    name: 'add_time_activity_to_bill'
                                }
                            }
                        },
                        testId: BillClientModalBodyTestIds.TimeActivities()
                    }),
                    /*#__PURE__*/ _jsx(Box, {
                        className: styles.contentPage,
                        children: /*#__PURE__*/ _jsxs(Column, {
                            gap: 16,
                            className: styles.modalContentWrapper,
                            children: [
                                /*#__PURE__*/ _jsx(DateRow, {
                                    name: "issueDate",
                                    label: billTexts.issueDateLabel(),
                                    compactInput: true,
                                    invoiceDraftDates: invoiceDraftDates,
                                    upcomingInvoiceContext: upcomingInvoiceContext,
                                    minDate: issueDateBounds.minDate,
                                    maxDate: issueDateBounds.maxDate,
                                    bi: {
                                        name: 'issue_date'
                                    },
                                    testId: BillClientModalBodyTestIds.DateRow()
                                }),
                                /*#__PURE__*/ _jsxs(Column, {
                                    gap: 16,
                                    className: styles.callouts,
                                    children: [
                                        /*#__PURE__*/ _jsx(CreditsWarningCallout, {
                                            totalCredits: totalCredits,
                                            testId: BillClientModalBodyTestIds.CreditsCallout()
                                        }),
                                        /*#__PURE__*/ _jsx(RelationshipEffectiveDateCallout, {
                                            issueDate: issueDate,
                                            effectiveDate: effectiveDate,
                                            testId: BillClientModalBodyTestIds.EffectiveDateCallout()
                                        })
                                    ]
                                }),
                                /*#__PURE__*/ _jsx(BillItemsTable, {
                                    relationshipEffectiveDate: normalizeEffectiveDate(effectiveDate),
                                    manualServicesBilling: props.manualServicesBilling,
                                    invoiceDateInfo: invoiceDateInfo,
                                    itemSelect: {
                                        agreementOptions,
                                        outOfScopeTemplates,
                                        formatTemplateCost,
                                        onSelectItem,
                                        onClearItem,
                                        onCreateNewItem,
                                        unselectedItemError: showUnselectedItemError ? addChargeTexts.itemRequiredError() : undefined
                                    },
                                    bi: {
                                        editButton: {
                                            name: 'edit_line'
                                        },
                                        removeButton: {
                                            name: 'remove_line'
                                        }
                                    },
                                    testId: BillClientModalBodyTestIds.Root()
                                }),
                                /*#__PURE__*/ _jsx(Box, {
                                    children: /*#__PURE__*/ _jsx(AddCharge, {
                                        agreementOptions: agreementOptions,
                                        outOfScopeTemplates: outOfScopeTemplates,
                                        formatTemplateCost: formatTemplateCost,
                                        triggerVariant: "dashedBox",
                                        triggerLabel: addChargeTexts.triggerLabel(),
                                        onSelect: onAddCharge,
                                        onCreateNew: onCreateNewCharge,
                                        bi: {
                                            trigger: {
                                                name: 'add_charge'
                                            },
                                            search: {
                                                name: 'add_charge_search'
                                            },
                                            createNew: {
                                                name: 'create_new_charge'
                                            }
                                        },
                                        testId: BillClientModalBodyTestIds.AddCharge()
                                    })
                                }),
                                /*#__PURE__*/ _jsx(AttachmentsList, {
                                    attachments: attachments.files,
                                    onRemove: attachments.onRemove,
                                    bi: {
                                        removeFile: {
                                            name: 'remove_attachment'
                                        }
                                    },
                                    testId: BillClientModalBodyTestIds.Attachments()
                                })
                            ]
                        })
                    })
                ]
            })
        })
    });
}
export function BillClientModalContent(props) {
    const { relationship } = useNonNullableRelationship(props.relationshipId);
    const { logBI } = useStoreActions();
    const dateContext = useInvoiceDateContext({
        invoiceDateInfo: props.invoiceDateInfo,
        earliestBillableDate: props.earliestBillableDate,
        effectiveDate: relationship.effectiveDate
    });
    const form = useBillClientForm({
        defaultItem: props.defaultItem,
        dateContext,
        startEmptyWhenUntouched: true
    });
    return /*#__PURE__*/ _jsx(FormProvider, _object_spread_props(_object_spread({}, form), {
        children: /*#__PURE__*/ _jsx(BillItemsProvider, {
            onBeforeRemoveItem: ()=>logBI(18405),
            children: /*#__PURE__*/ _jsx(BillClientModalBody, {
                props: props,
                form: form,
                dateContext: dateContext
            })
        })
    }));
}
export function BillClientModal(props) {
    const featureFlags = useFeatureFlags();
    const { relationship } = useNonNullableRelationship(props.relationshipId);
    if (featureFlags[FeatureFlag.BILL_MODAL_V2] !== true) {
        return /*#__PURE__*/ _jsx(BillClientModalOld, _object_spread({}, props));
    }
    const isApproved = relationshipModel.isApproved(relationship);
    return /*#__PURE__*/ _jsx(BIRelationshipScopeProvider, {
        relationshipScope: {
            id: props.relationshipId,
            isApproved
        },
        children: /*#__PURE__*/ _jsx(BillClientModalContent, _object_spread({}, props))
    });
}
